import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useForm } from "react-hook-form";
import { InputForm } from "../../components/molecules/forms/index";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store";
import { useEffect, useMemo, useState } from "react";
import DetailDataSkeleton from "../../components/template/skeleton/DetailDataSkeleton";
import Swal from "sweetalert2";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useUpdateProfilePasswordMutation } from "../../store/feature/service/userApiSlice";
import confirmPasswordProfile from "../../utils/validation/confirmPasswordProfile";

const ChangePassword = () => {

  // REGION: INIT VARIABLES
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [updateProfilePassword, {
    data: updatePayload,
    error: updateErrorPayload,
    isLoading : updateLoading,
    isSuccess: updateSuccess,
    isError: updateError,
  }] = useUpdateProfilePasswordMutation();
  const [isShowPass, setIsShowPass] = useState(false);
   
  // REGION: INIT FORM DATA
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    uid: string;
    oldPassword: string;
    password: string;
    confirmPassword: string;
  }>({
    reValidateMode: "onChange",
    resolver: yupResolver(confirmPasswordProfile),
    defaultValues: {
      uid: "",
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
  });

  const endAdornment = useMemo(() => {
    if (isShowPass) {
      return <VisibilityIcon />;
    }
    return <VisibilityOffIcon />;
  }, [isShowPass]);

  const toggleShowPassword = () => {
    setIsShowPass((prev) => !prev);
  };

  // REGION: SET FORM DATA
  useEffect(() => {
    reset({
      oldPassword: "",
      password: "",
      confirmPassword: "",
    });
  }, [ ]);

  // REGION: DISPATCH INSERT & UPDATE DATA
  const onSubmit = async (e: any) => {
    if(e.password === e.confirmPassword) {
      Swal.fire({
        title: "Submit Confirmation",
        text: "Are you sure you want to change password?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#808080",
        confirmButtonText: "Yes",
        reverseButtons: true,
      }).then( async (result) => {
        if (result.isConfirmed) {
          await updateProfilePassword({
            oldPassword: e.oldPassword,
            newPassword: e.password
          });
        }
      });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Password Not Match",
        showCancelButton: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#051438",
        text: "Password Not Match",
        didOpen: () => Swal.getConfirmButton()?.focus(),
      });
    }
  };

  if (updateSuccess) {
    Swal.fire({
      icon: "success",
      title: "Success Update Password",
      showCancelButton: false,
      confirmButtonText: "OK",
      confirmButtonColor: "#051438",
      text: "Success Update Password",
      didOpen: () => Swal.getConfirmButton()?.focus(),
    }).then(() => {
      navigate("/");
    });
  }

  useEffect(() => {
    if (updateError) {
      let message = "Error Update Password, Please try again"
      try {
        const dataError: any = updateErrorPayload
        message = dataError?.data.errors[0].detail
      } catch(e){}
      Swal.fire({
        icon: "error",
        title: "Error Update Password",
        showCancelButton: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#051438",
        text: message,
        didOpen: () => Swal.getConfirmButton()?.focus(),
      });
    }
  }, [updateError]);

  // REGION: RETURN TSX
  return (
    <DetailDataSkeleton
    isLoading={false}
    isLoadingEvent={updateLoading}
    inputCount={6}
    titleWidth={'350px'}>
    <Box component="form" onSubmit={handleSubmit(onSubmit)} paddingY="20px">
      <Typography variant="h3" fontFamily="Open Sans" mb={3}>
        Change Password
      </Typography>

      <InputForm
          name="oldPassword"
          label="Old Password"
          placeholder="Password"
          disabled={false}
          isLoading={false}
          control={control}
          errors={errors}
          maxLength={500}
          required
          showEndAdornment
          sxEndAdornment={{
            backgroundColor: 'inherit',
            border: 'none',
            cursor: 'pointer',
            width: '4rem',
          }}
          endAdornment={endAdornment}
          onClickEndAdornment={toggleShowPassword}
          type={isShowPass ? 'text' : 'password'}
        />

        <InputForm
          name="password"
          label="Password"
          placeholder="Password"
          disabled={false}
          isLoading={false}
          control={control}
          errors={errors}
          maxLength={500}
          required
          showEndAdornment
          sxEndAdornment={{
            backgroundColor: 'inherit',
            border: 'none',
            cursor: 'pointer',
            width: '4rem',
          }}
          endAdornment={endAdornment}
          onClickEndAdornment={toggleShowPassword}
          type={isShowPass ? 'text' : 'password'}
        />

        <InputForm
          name="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm Password"
          disabled={false}
          isLoading={false}
          control={control}
          errors={errors}
          maxLength={500}
          required
          showEndAdornment
          sxEndAdornment={{
            backgroundColor: 'inherit',
            border: 'none',
            cursor: 'pointer',
            width: '4rem',
          }}
          endAdornment={endAdornment}
          onClickEndAdornment={toggleShowPassword}
          type={isShowPass ? 'text' : 'password'}
        />

      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        justifyContent="flex-end">
        <Button
          variant="contained"
          color="gray"
          onClick={() => navigate("/")}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          Submit
        </Button>
      </Stack>
    </Box>
    </DetailDataSkeleton>
  );
};

export default ChangePassword;
